import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import { useNavigate } from "react-router";
import SearchField from "../../../interface/shared/searchfield";
import Typography from "@mui/material/Typography";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import DashboardLayout from "../../layouts/dashboard";
import Alert from "@mui/material/Alert";
import Skeleton from "@mui/material/Skeleton";
import { ProviderProducts, useProducts } from "../../../api/products";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import { FadeIn } from "../../../interface/shared/animations";
import { ProviderStockWarnings, useStockWarnings } from "../../../api/stockwarnings";
import { Chip } from "@mui/material";
import TimeAgo from 'react-time-ago';
import "react-time-ago/locale/en"

export default function StockExpiryWarnings() {
    return (
        <DashboardLayout>
            <ProviderStockWarnings>
                <StockExpiryWarningsContent />
            </ProviderStockWarnings>
        </DashboardLayout>
    );
}

function StockExpiryWarningsContent() {
    const { stockExpiryWarnings, errors, loading, fetchStockWarnings, page, perPage, total, setPage, setPerPage, searchTerm, setSearchTerm, sortField, sortOrder, setSort } = useStockWarnings();
    const navigate = useNavigate();

    return (
        <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
            <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                sx={{ mb: 3 }}
            >
                <Box>
                    <Typography variant="h5" gutterBottom>
                        Stock Expiry Warnings
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        All stock expiry warnings in the warehouse.
                    </Typography>
                </Box>
                <SearchField
                    placeholder="Search stock expiry warnings by product name, code or main code"
                    value={searchTerm}
                    onChange={setSearchTerm}
                />
            </Stack>
            {errors.length > 0 && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    action={
                        <Button color="inherit" size="small" onClick={fetchStockWarnings}>
                            Retry
                        </Button>
                    }
                >
                    {errors.join(" ")}
                </Alert>
            )}
            <Card variant="outlined">
                <CardContent>
                    {loading ? (
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Code</TableCell>
                                    <TableCell>Main Code</TableCell>
                                    <TableCell>Product</TableCell>
                                    <TableCell>Size</TableCell>
                                    <TableCell>Pack</TableCell>
                                    <TableCell>Weight</TableCell>
                                    <TableCell align="center">Expiry</TableCell>
                                    <TableCell align="right"></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Array.from({ length: 5 }).map((_, i) => (
                                    <TableRow key={i}>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell align="center">
                                            <Box alignItems="center" justifyContent="center" display="flex"><Skeleton variant="circular" width={20} height={20} /></Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box alignItems="center" justifyContent="end" display="flex">
                                                <Skeleton variant="rounded" width={70} height={30} />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <FadeIn>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sortDirection={sortField === "code" ? sortOrder : false}>
                                            <TableSortLabel
                                                active={sortField === "code"}
                                                direction={sortField === "code" ? sortOrder : "asc"}
                                                onClick={() => setSort("code")}
                                            >
                                                Code
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>Product</TableCell>
                                        <TableCell>Location</TableCell>
                                        <TableCell>Level</TableCell>
                                        <TableCell align="center" sortDirection={sortField === "active" ? sortOrder : false}>
                                            <TableSortLabel
                                                active={sortField === "active"}
                                                direction={sortField === "active" ? sortOrder : "asc"}
                                                onClick={() => setSort("active")}
                                            >
                                                Expiry
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="right"></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {stockExpiryWarnings.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8}>
                                                <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                                                    No stock expiry warnings found.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : stockExpiryWarnings.map((warning) => (
                                        <TableRow key={`${warning.stock.id}`}>
                                            <TableCell>{warning.stock.product.code}</TableCell>
                                            <TableCell>{warning.stock.product.name}</TableCell>
                                            <TableCell>{warning.stock.location.name}</TableCell>
                                            <TableCell>{warning.stock.level}</TableCell>
                                            <TableCell align="center">
                                                {warning.expired ? (
                                                    <Chip label={warning.expires_at != null ? <TimeAgo date={warning.expires_at} locale="en" /> : '-'} color="error" />
                                                ) : warning.warning_level === 1 ? (
                                                    <Chip label={warning.expires_at != null ? <TimeAgo date={warning.expires_at} locale="en" /> : '-'} color="warning" />
                                                ) : (
                                                    <Chip label={warning.expires_at != null ? <TimeAgo date={warning.expires_at} locale="en" /> : '-'} color="success" />
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    endIcon={<VisibilityRoundedIcon fontSize="small" />}
                                                    onClick={() => navigate(`/products/${warning.stock.product.id}`)}
                                                >
                                                    View
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <TablePagination
                                component="div"
                                count={total}
                                page={page - 1}
                                onPageChange={(_, newPage) => setPage(newPage + 1)}
                                rowsPerPage={perPage}
                                onRowsPerPageChange={(e) => {
                                    setPerPage(parseInt(e.target.value, 10));
                                    setPage(1);
                                }}
                                rowsPerPageOptions={[10, 15, 25, 50]}
                            />
                        </FadeIn>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}